<script setup lang="ts">
/**
 * A queued Slow (Gemma) turn's progress, in the chat bubble (MAR-1424; mockup on the ticket).
 *
 * Four stages, always all four and always in this order: En cola → Iniciando el modelo → Investigando
 * leyes → Escribiendo la respuesta. The ones before the current stage are done, so on a warm GPU
 * "Iniciando el modelo" shows done the moment the turn starts researching — the worker never writes
 * that stage (AC2). The current stage carries one detail line: the place in line while queued (the
 * position counts down, AC1), the cold-start wait, or what the running tool is reading.
 *
 * Below it, "Envíamelo por correo" (AC3): pressing it asks the parent to mark the job; once marked the
 * button becomes a note saying where the answer goes and that the page can be closed. `ChatMessages`
 * mounts this only while the turn streams, so a finished turn shows no button (AC4).
 *
 * Stateless: everything shown comes from `progress`, which the stream sets off the job doc.
 */
import { Check, Mail } from 'lucide-vue-next'
import { QUEUED_CHAT_STAGES, type QueuedChatStage } from '~~/shared/chat/queuedChatTurn'
import type { QueuedTurnView } from '~/types/web-chat'

const props = defineProps<{
  progress: QueuedTurnView
  /** The running tool's label — what "Investigando leyes" is reading right now. */
  detail?: string | null
}>()
const emit = defineEmits<{ 'request-email': [] }>()

const { t } = useI18n()

const STAGE_KEYS: Record<QueuedChatStage, string> = {
  queued: 'chat.queuedTurn.stages.queued',
  starting_model: 'chat.queuedTurn.stages.startingModel',
  researching: 'chat.queuedTurn.stages.researching',
  writing: 'chat.queuedTurn.stages.writing',
}

const currentIndex = computed(() => Math.max(0, QUEUED_CHAT_STAGES.indexOf(props.progress.stage)))

const rows = computed(() => QUEUED_CHAT_STAGES.map((stage, index) => ({
  stage,
  label: t(STAGE_KEYS[stage]),
  state: index < currentIndex.value ? 'done' : index === currentIndex.value ? 'active' : 'pending',
})))

const aheadLine = computed(() => {
  const position = props.progress.position
  if (!position) return ''
  const ahead = position - 1
  if (ahead <= 0) return t('chat.queuedTurn.aheadNone')
  if (ahead === 1) return t('chat.queuedTurn.aheadOne')
  return t('chat.queuedTurn.aheadMany', { n: ahead })
})

function detailFor(stage: QueuedChatStage): string {
  if (stage === 'queued') return aheadLine.value
  if (stage === 'starting_model') return t('chat.queuedTurn.coldStart')
  if (stage === 'researching') return props.detail?.trim() ?? ''
  return ''
}
</script>

<template>
  <div class="mt-1 max-w-md" data-testid="queued-turn-progress">
    <ol class="rounded-xl border border-border bg-card px-4 py-3 space-y-2.5" aria-live="polite">
      <li
        v-for="row in rows"
        :key="row.stage"
        data-testid="queued-stage"
        :data-state="row.state"
        class="flex items-start gap-2.5"
      >
        <span class="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true">
          <span v-if="row.state === 'done'" class="flex h-4 w-4 items-center justify-center rounded-full bg-accent-ink text-on-accent-ink">
            <Check class="h-2.5 w-2.5" :stroke-width="3" />
          </span>
          <span v-else-if="row.state === 'active'" class="queued-spinner h-4 w-4 rounded-full border-[1.5px] border-muted-foreground/30 border-t-foreground" />
          <span v-else class="h-4 w-4 rounded-full border-[1.5px] border-muted-foreground/30" />
        </span>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <span
              data-testid="queued-stage-label"
              class="text-sm"
              :class="row.state === 'active' ? 'font-medium text-foreground' : row.state === 'done' ? 'text-foreground' : 'text-muted-foreground'"
            >{{ row.label }}</span>
            <span
              v-if="row.stage === 'queued' && row.state === 'active' && progress.position"
              data-testid="queued-position"
              class="rounded-full bg-accent-light px-2 py-0.5 text-xs font-medium text-accent-light-foreground"
            >{{ t('chat.queuedTurn.position', { n: progress.position }) }}</span>
          </div>
          <p
            v-if="row.state === 'active' && detailFor(row.stage)"
            class="mt-0.5 text-[13px] leading-snug text-muted-foreground truncate"
          >{{ detailFor(row.stage) }}</p>
        </div>
      </li>
    </ol>

    <p
      v-if="progress.emailRequested"
      data-testid="queued-email-confirmation"
      class="mt-2 rounded-xl bg-accent-light px-4 py-2.5 text-[13px] leading-snug text-accent-light-foreground"
    >
      <Check class="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />
      <template v-if="progress.email">{{ t('chat.queuedTurn.emailConfirmed', { email: progress.email }) }}</template>
      <template v-else>{{ t('chat.queuedTurn.emailConfirmedFallback') }}</template>
      {{ t('chat.queuedTurn.emailLeave') }}
    </p>
    <div v-else class="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
      <span>{{ t('chat.queuedTurn.emailPrompt') }}</span>
      <button
        type="button"
        data-testid="queued-email-button"
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-default"
        :disabled="progress.emailPending"
        @click="emit('request-email')"
      >
        <Mail class="h-3.5 w-3.5" aria-hidden="true" />
        {{ t('chat.queuedTurn.emailButton') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.queued-spinner {
  animation: queued-spin 0.9s linear infinite;
}
@keyframes queued-spin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .queued-spinner { animation: none; }
}
</style>
